import { FunctionComponent, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu";
import { useUsers, User } from "../contexts/UserContext";
import styles from "./UsersScreen.module.css";

// AG Grid imports
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Define User interface locally for AG Grid typing
interface UserForGrid {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
    company?: {
        name: string;
        logo?: string;
    };
}

// Form data interface that extends User to include password for creation
interface UserFormData extends Partial<User> {
    password?: string;
}

interface ModalProps {
    type: "add" | "edit" | "invite" | "delete" | null;
    userData?: User | null;
}

const UsersScreen: FunctionComponent = () => {
    const navigate = useNavigate();
    const { users, loading, error, pagination, fetchUsers, addUser, editUser, deleteUser, inviteUser } = useUsers();    // Local state for user management
    const [modalState, setModalState] = useState<ModalProps>({ type: null });
    const [formData, setFormData] = useState<UserFormData>({});
    const [inviteData, setInviteData] = useState({
        email: "",
        role: "user",
        companyId: 1 // Default company ID
    });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [quickFilterText, setQuickFilterText] = useState('');

    // Load users on component mount
    useEffect(() => {
        fetchUsers(currentPage);
    }, [fetchUsers, currentPage]);

    // Handle input changes for the form
    const handleInputChange = (field: string, value: string) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    // Handle input changes for the invite form
    const handleInviteInputChange = (field: string, value: string) => {
        setInviteData({
            ...inviteData,
            [field]: field === "companyId" ? parseInt(value) : value
        });
    };    // Open modal for adding a new user
    const handleOpenAddModal = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            role: "user",
            companyId: 1, // Default company ID
            status: "active"
        });
        setModalState({ type: "add" });
    };

    // Open modal for inviting a new user
    const handleOpenInviteModal = () => {
        setInviteData({
            email: "",
            role: "user",
            companyId: 1 // Default company ID
        });
        setModalState({ type: "invite" });
    };    // Open modal for editing a user
    const handleOpenEditModal = (user: User) => {
        setFormData({
            ...user,
            password: "" // Clear password field for editing
        });
        setModalState({ type: "edit", userData: user });
    };

    // Open modal for deleting a user
    const handleOpenDeleteModal = (user: User) => {
        setModalState({ type: "delete", userData: user });
    };

    // Close any open modal
    const handleCloseModal = () => {
        setModalState({ type: null });
    };
    // Submit handler for add/edit user form
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Password validation for add user
        if (modalState.type === "add" && formData.password && formData.password.length < 6) {
            alert("Password must be at least 6 characters long");
            return;
        }

        // Password validation for edit user (only if password is provided)
        if (modalState.type === "edit" && formData.password && formData.password.length < 6) {
            alert("Password must be at least 6 characters long");
            return;
        } if (modalState.type === "add") {
            const result = await addUser(formData);
            if (result) {
                handleCloseModal();
                fetchUsers(currentPage);
            }
        } else if (modalState.type === "edit" && modalState.userData) {
            // Create a copy of formData and remove password field if it's empty
            const updateData = { ...formData };
            if (!updateData.password || updateData.password.trim() === "") {
                delete updateData.password;
            }

            const result = await editUser(modalState.userData.id, updateData);
            if (result) {
                handleCloseModal();
                fetchUsers(currentPage);
            }
        } else if (modalState.type === "invite") {
            const result = await inviteUser(
                inviteData.email,
                inviteData.role,
                inviteData.companyId
            );
            if (result) {
                handleCloseModal();
                fetchUsers(currentPage);
            }
        } else if (modalState.type === "delete" && modalState.userData) {
            const result = await deleteUser(modalState.userData.id);
            if (result) {
                handleCloseModal();
                fetchUsers(currentPage);
            }
        }
    };// Render status badge based on user status
    const renderStatusBadge = (status: string) => {
        const statusClass = styles[status] || styles.inactive;
        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    // Cell Renderer for Status column
    const StatusRenderer: FunctionComponent<ICellRendererParams<UserForGrid>> = (props) => {
        const { data } = props;
        if (!data) return null;
        return renderStatusBadge(data.status);
    };

    // Cell Renderer for Actions column
    const UserActionsRenderer: FunctionComponent<ICellRendererParams<UserForGrid> & {
        onEditClick: (user: User) => void;
        onDeleteClick: (user: User) => void;
    }> = (props) => {
        const { data, onEditClick, onDeleteClick } = props;

        if (!data) {
            return null;
        }

        const handleEditButtonClick = () => {
            onEditClick(data as User);
        };

        const handleDeleteButtonClick = () => {
            onDeleteClick(data as User);
        };

        return (
            <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: '8px' }}>
                <button
                    className={`${styles.actionButton} ${styles.editButton}`}
                    onClick={handleEditButtonClick}
                    title="Edit User"
                >
                    Edit
                </button>
                <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={handleDeleteButtonClick}
                    title="Delete User"
                >
                    Delete
                </button>
            </div>
        );
    };

    const columnDefs = useMemo((): ColDef<UserForGrid>[] => {
        return [
            {
                headerName: "Name",
                valueGetter: params => `${params.data?.firstName || ''} ${params.data?.lastName || ''}`.trim(),
                flex: 1,
                minWidth: 180,
                sortable: true,
                filter: false
            },
            { headerName: "Email", field: "email", flex: 1, minWidth: 200, sortable: true, filter: false },
            {
                headerName: "Role",
                field: "role",
                valueFormatter: params => params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : 'N/A',
                width: 120,
                sortable: true,
                filter: false
            },
            {
                headerName: "Status",
                cellRenderer: StatusRenderer,
                width: 120,
                sortable: true,
                filter: false,
            },
            {
                headerName: "Company",
                valueGetter: params => params.data?.company?.name || '-',
                width: 150,
                sortable: true,
                filter: false
            },
            {
                headerName: "Actions",
                cellRenderer: UserActionsRenderer,
                cellRendererParams: {
                    onEditClick: handleOpenEditModal,
                    onDeleteClick: handleOpenDeleteModal,
                },
                width: 180,
                filter: false,
                sortable: false,
                resizable: false,
            },
        ];
    }, []);

    const onQuickFilterTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuickFilterText(event.target.value);
    };

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: false, // Disabled filtering by default
        resizable: true,
    }), []);

    // Render the appropriate modal based on modalState.type
    const renderModal = () => {
        if (!modalState.type) return null;

        let title = "";
        let content = null;

        switch (modalState.type) {
            case "add":
                title = "Add New User";
                content = (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="firstName">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                value={formData.firstName || ""}
                                onChange={(e) => handleInputChange("firstName", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="lastName">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                value={formData.lastName || ""}
                                onChange={(e) => handleInputChange("lastName", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={formData.email || ""}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                value={formData.password || ""}
                                onChange={(e) => handleInputChange("password", e.target.value)}
                                required
                                minLength={6}
                                placeholder="Enter password (minimum 6 characters)"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="role">Role</label>
                            <select
                                id="role"
                                value={formData.role || "user"}
                                onChange={(e) => handleInputChange("role", e.target.value)}
                            >
                                <option value="admin">Admin</option>
                                <option value="user">User</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                value={formData.status || "active"}
                                onChange={(e) => handleInputChange("status", e.target.value)}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </form>
                );
                break;
            case "edit":
                title = "Edit User";
                content = (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="firstName">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                value={formData.firstName || ""}
                                onChange={(e) => handleInputChange("firstName", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="lastName">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                value={formData.lastName || ""}
                                onChange={(e) => handleInputChange("lastName", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={formData.email || ""}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                disabled
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="password">New Password (optional)</label>
                            <input
                                type="password"
                                id="password"
                                value={formData.password || ""}
                                onChange={(e) => handleInputChange("password", e.target.value)}
                                minLength={6}
                                placeholder="Leave blank to keep current password"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="role">Role</label>
                            <select
                                id="role"
                                value={formData.role || "user"}
                                onChange={(e) => handleInputChange("role", e.target.value)}
                            >
                                <option value="admin">Admin</option>
                                <option value="user">User</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                value={formData.status || "active"}
                                onChange={(e) => handleInputChange("status", e.target.value)}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </form>
                );
                break;
            case "invite":
                title = "Invite User";
                content = (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={inviteData.email}
                                onChange={(e) => handleInviteInputChange("email", e.target.value)}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="role">Role</label>
                            <select
                                id="role"
                                value={inviteData.role}
                                onChange={(e) => handleInviteInputChange("role", e.target.value)}
                            >
                                <option value="admin">Admin</option>
                                <option value="user">User</option>
                            </select>
                        </div>
                    </form>
                );
                break;
            case "delete":
                title = "Delete User";
                content = (
                    <div>
                        <p>Are you sure you want to delete user {modalState.userData?.firstName} {modalState.userData?.lastName}?</p>
                        <p>This action cannot be undone.</p>
                    </div>
                );
                break;
            default:
                break;
        }

        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modal}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.modalTitle}>{title}</h2>
                        <button className={styles.closeButton} onClick={handleCloseModal}>×</button>
                    </div>
                    <div className={styles.modalContent}>
                        {content}
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelButton} onClick={handleCloseModal}>Cancel</button>
                        <button className={styles.submitButton} onClick={handleSubmit}>
                            {modalState.type === "delete" ? "Delete" : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }; return (
        <div>
            <AdminSideMenu />
            <AdminTopMenu />
            <div className={styles.container}>
                <div className={styles.mainContent}>                <div className={styles.header}>
                    <h1 className={styles.headerTitle}>User Management</h1>
                    <div>
                        <button className={styles.addUserButton} onClick={handleOpenAddModal}>
                            + Add User
                        </button>
                    </div>
                </div>

                    <input
                        type="text"
                        placeholder="Search users..."
                        value={quickFilterText}
                        onChange={onQuickFilterTextChanged}
                        className={styles.searchInput}
                        style={{ marginBottom: '15px', padding: '10px 15px', width: 'clamp(300px, 40%, 500px)', border: '1px solid #ccc', borderRadius: '5px', fontSize: '1rem', boxSizing: 'border-box' }}
                    />

                    {error && (
                        <div className={styles.errorContainer}>
                            <p>{error}</p>
                        </div>
                    )}

                    <div className={styles.usersContainer}>
                        {loading && (
                            <div className={styles.loadingContainer}>
                                <div className={styles.loadingSpinner}></div>
                                <p>Loading users...</p>
                            </div>
                        )}
                        {error && (
                            <div className={styles.errorContainer}>
                                <p>Error: {error}</p>
                            </div>
                        )}
                        {!loading && !error && (
                            <div
                                className={`ag-theme-alpine ${styles.agGridContainer}`}
                                style={{ height: 'calc(100vh - 280px)', width: '100%' }}
                            >
                                <AgGridReact<UserForGrid>
                                    rowData={users}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    pagination={true}
                                    paginationPageSize={20}
                                    paginationPageSizeSelector={[10, 20, 50, 100]}
                                    quickFilterText={quickFilterText}
                                    overlayNoRowsTemplate={
                                        '<span style="padding: 10px; border: 1px solid #ddd; background: #f9f9f9; border-radius: 4px; font-style: italic;">No users found. Add users to get started.</span>'
                                    }
                                    rowHeight={48}
                                />
                            </div>
                        )}
                    </div>

                    {renderModal()}

                </div>
            </div>
        </div>
    );
};

export default UsersScreen;
